<template>
  <slot v-if="$slots.default" :show-connect-shop-dialog="showConnectShopDialog" />
  <Button
    v-else
    :label="props.label"
    :class="cx('flex-row-reverse gap-x-2', $attrs.class)"
    :pt="{
      label: {
        class: 'text-regular-medium'
      }
    }"
    :severity="props.severity"
    @click="showConnectShopDialog()"
  >
    <template v-if="props.showIcon" #icon>
      <Icon class="size-3.5" name="market" />
    </template>
  </Button>
</template>

<script lang="ts" setup>
import { useUrlSearchParams } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import Button from 'primevue/button';
import { useConfirm } from 'primevue/useconfirm';
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

import Icon from '@/common/components/atoms/Icon';
import { mixPanelEvents } from '@/common/constants';
import { useEtsyMarketplaceStore } from '@/common/stores/marketplaces';
import { useSubscriptionStore } from '@/common/stores/subscription';
import { track } from '@/helpers/analytics/mixpanel';
import { cx } from '@/helpers/cva';

const props = withDefaults(defineProps<Props>(), {
  redirectTo: 'dashboard',
  module: 'Unknown',
  label: 'Connect Etsy Shop',
  showIcon: true,
  severity: 'primary',
});

const { quotaNoneDaily } = storeToRefs(useSubscriptionStore());

const confirm = useConfirm();
const etsyStore = useEtsyMarketplaceStore();
const params = useUrlSearchParams('history');

interface Props {
  redirectTo?: string;
  module?: string;
  label?: string;
  showIcon?: boolean;
  severity?: 'primary' | 'secondary' | 'warning' | 'success' | 'danger' | 'info';
}

const route = useRoute();
const page_slug = computed(() => route?.meta?.metaTags?.find((tag) => tag.page_slug)?.page_slug ?? '');
const source = computed(() => page_slug.value.replaceAll(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()));

interface ConnectEtsyShopSlots {
  default?: (props: { showConnectShopDialog: () => void }) => void;
}

defineSlots<ConnectEtsyShopSlots>();

const showConnectShopDialog = () => {
  confirm.require({
    group: 'connect_shop',
    accept: () => checkQuotaShop(),
    reject: () => trackCancelButton(),
  });

  // Track the connection initiation
  track(mixPanelEvents.STARTED_SHOP_CONNECTION, {
    Module: props.module,
    'Source': source.value,
    'Connection Type': 'Etsy Shop',
    'Erank Version': 3,
    'Page Title': route.meta?.title || 'Unknown',
  });
};

const trackCancelButton = () => {
  track(mixPanelEvents.CLICK_CONNECT_SHOP_CANCEL_BUTTON, {
    Module: props.module,
    'Source': mixPanelEvents.CANCELLED_AT_MODAL,
    'Connection Type': 'Etsy Shop',
    'Erank Version': 3,
  });
};

const checkQuotaShop = () => {
  if(quotaNoneDaily.value.connected_shops.quota_exceeded) {
    confirm.require({
      group: 'reached_quota_connected_shop',
      accept: () => window.location.href = `/plans`,
    });
  } else {
    return etsyStore.connectShop('etsy', props.redirectTo);
  }
};

onMounted(() => {
  if (params.success && params.shop_name) {
    // Track the completed Etsy connection
    track(mixPanelEvents.CONNECTED_SHOP, {
      Module: props.module,
      'Source': source.value,
      'Erank Version': 3,
      'Page Title': route.meta?.title || 'Unknown',
    });

    confirm.require({
      group: 'success_connect',
      header: `${params.shop_name} Connected`,
    });
  }

  if (params.success == 'false' && params.access_grant == 'refused') {
    confirm.require({
      group: 'failed_connect_shop',
      accept: () => (etsyStore.connectShop('etsy', props.redirectTo), track(mixPanelEvents.STARTED_SHOP_CONNECTION, {
        Module: props.module,
        'Source': source.value,
        'Connection Type': 'Etsy Shop',
        'Erank Version': 3,
      })),
      reject: () => window.location.href = `/${props.redirectTo}`,
    });
  }
});
</script>

<style lang="scss" scoped>
.here, .privacy-link {
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}
</style>
